// AdminMenu.js — Page d'accueil Administration avec tuiles
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  BarChart3, Users, Building2, FileText, MapPin, Settings, ShieldCheck, CalendarCheck
} from 'lucide-react';

const TILES = [
  {
    to: '/admin',
    icon: BarChart3,
    title: 'Vue Équipe',
    desc: 'Tableau de bord en temps réel des présences du jour',
    roles: ['admin', 'manager'],
    accent: 'from-blue-500 to-indigo-600',
  },
  {
    to: '/admin/employees',
    icon: Users,
    title: 'Employés',
    desc: 'Gérer les comptes, rôles et données des employés',
    roles: ['admin'],
    accent: 'from-emerald-500 to-teal-600',
  },
  {
    to: '/admin/teams',
    icon: Building2,
    title: 'Équipes',
    desc: 'Créer et organiser les équipes et départements',
    roles: ['admin', 'manager'],
    accent: 'from-amber-500 to-orange-600',
  },
  {
    to: '/admin/reports',
    icon: FileText,
    title: 'Rapports',
    desc: 'Exports Excel/PDF des présences et statistiques',
    roles: ['admin', 'manager'],
    accent: 'from-violet-500 to-purple-600',
  },
  {
    to: '/admin/leaves',
    icon: CalendarCheck,
    title: 'Congés',
    desc: 'Valider ou refuser les demandes de congés',
    roles: ['admin', 'manager'],
    accent: 'from-emerald-500 to-teal-600',
  },
  {
    to: '/admin/locations',
    icon: MapPin,
    title: 'Localisations',
    desc: 'Géofencing GPS des bureaux et sites autorisés',
    roles: ['admin'],
    accent: 'from-rose-500 to-pink-600',
  },
  {
    to: '/admin/settings',
    icon: Settings,
    title: 'Paramètres',
    desc: 'Configuration générale de la plateforme',
    roles: ['admin'],
    accent: 'from-slate-500 to-slate-700',
  },
];

export default function AdminMenu() {
  const { user } = useAuth();
  const tiles = TILES.filter(t => t.roles.includes(user?.role));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-md">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Menu Administration</h1>
          <p className="text-sm text-gray-500">Accédez rapidement aux modules de gestion</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        {tiles.map(({ to, icon: Icon, title, desc, accent }) => (
          <Link
            key={to}
            to={to}
            className="group relative bg-white rounded-2xl p-5 border border-gray-200 hover:border-amber-400 hover:shadow-xl transition-all duration-200 overflow-hidden"
          >
            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} />
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shadow-md flex-shrink-0 group-hover:scale-110 transition-transform`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-800 group-hover:text-amber-700 transition-colors">{title}</h3>
                <p className="text-sm text-gray-500 mt-1 leading-snug">{desc}</p>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-400 uppercase tracking-wider">Accéder</span>
              <span className="text-amber-600 group-hover:translate-x-1 transition-transform">→</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
